<template>
  <ElTag
    :type="tagType"
    :size="size"
    :effect="effect"
    :closable="closable"
    :disable-transitions="disableTransitions"
    :hit="hit"
    :color="color"
    :round="round"
    class="dh-tag"
    :class="[
      {
        'dh-tag--clickable': clickable,
        'dh-tag--bordered': bordered
      }
    ]"
    @close="handleClose"
    @click="handleClick"
  >
    <slot>
      {{ text }}
    </slot>
  </ElTag>
</template>

<script setup lang="ts">
/**
 * 通用标签组件
 * 基于 Element Plus Tag 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { computed } from 'vue'
import type { TagProps } from 'element-plus'

/**
 * 组件属性接口
 */
interface Props {
  /** 标签文本 */
  text?: string
  /** 标签类型 */
  type?: TagProps['type']
  /** 标签尺寸 */
  size?: TagProps['size']
  /** 标签主题 */
  effect?: TagProps['effect']
  /** 是否可关闭 */
  closable?: boolean
  /** 是否禁用渐变动画 */
  disableTransitions?: boolean
  /** 是否有边框描边 */
  hit?: boolean
  /** 背景色 */
  color?: string
  /** 是否圆形 */
  round?: boolean
  /** 是否可点击 */
  clickable?: boolean
  /** 是否显示边框 */
  bordered?: boolean
}

/**
 * 组件事件接口
 */
interface Emits {
  /** 关闭事件 */
  close: []
  /** 点击事件 */
  click: [event: MouseEvent]
}

// 定义属性
const props = withDefaults(defineProps<Props>(), {
  text: '',
  type: 'primary',
  size: 'default',
  effect: 'light',
  closable: false,
  disableTransitions: false,
  hit: false,
  color: '',
  round: false,
  clickable: false,
  bordered: false
})

// 定义事件
const emit = defineEmits<Emits>()

/**
 * 计算标签类型
 */
const tagType = computed(() => {
  return props.type
})

/**
 * 处理关闭事件
 */
const handleClose = (): void => {
  emit('close')
}

/**
 * 处理点击事件
 */
const handleClick = (event: MouseEvent): void => {
  if (props.clickable) {
    emit('click', event)
  }
}
</script>

<style scoped>
.dh-tag {
  transition: all 0.3s ease;
}

.dh-tag--clickable {
  cursor: pointer;
}

.dh-tag--clickable:hover {
  opacity: 0.8;
  transform: translateY(-1px);
}

.dh-tag--bordered {
  border: 1px solid var(--el-border-color);
}
</style>
